import { HolidayTripsListApi, HolidayTripsQueryFilter, HolidayTripsQueryRecord, HolidayTripsQueryType } from 'api/holiday-trips-list';
import { autoinject } from 'aurelia-framework';
import { RouteConfig, Router } from 'aurelia-router';
import { watch } from 'aurelia-watch-decorator';
import { tryExecute } from 'framework/error_handling/error-utils';
import { log } from 'framework/script_utilities/dev-decorators';
import { TaskCounter } from 'framework/script_utilities/task-executor';
import { TextHighlightValueConverter } from 'framework/value_converters/text-highlight';
import { QueryParams } from 'model/essential-types';
import * as E from 'support/errors';
import { BATCH_SIZE, HOME_ROUTE } from 'support/settings';
import { QueryMemory } from 'support/shared_models/query-memory';

@autoinject
export class HolidayTripsCommonList {
  queryType: HolidayTripsQueryType = null;
  trips: HolidayTripsQueryRecord[] = null;
  lastQueryId = 0;
  loaded = false;

  constructor(private api: HolidayTripsListApi, private router: Router, public queryMemory: QueryMemory<HolidayTripsQueryFilter>, private taskCounter: TaskCounter) { }

  @log('debug')
  activate(_params, route: RouteConfig) {
    this.queryType = route.settings.queryType as HolidayTripsQueryType;
  }

  bind() {
    this.queryTrips();
  }

  filterMutated() {
    this.taskCounter.execute(() => tryExecute(() => this.queryTrips(), E.FILTER_ERROR_MESSAGE, E.FILTER_ERROR_TITLE, true));
  }

  @watch((x: HolidayTripsCommonList) => x.queryMemory.sortExpression)
  sortExpressionChanged() {
    this.taskCounter.execute(() => tryExecute(() => this.queryTrips(), E.SORT_ERROR_MESSAGE, E.SORT_ERROR_TITLE, true));
  }

  async queryTrips() {
    const queryId = ++this.lastQueryId;
    const trips = await this.fetchTrips(0, this.batchSize);
    if (queryId === this.lastQueryId) {
      this.trips = trips;
      this.loaded = true;
    }
  }

  async fetchTrips(skip: number, take: number) {
    const highlight = this.queryMemory.filter.text;
    const params: QueryParams = { sortExpression: this.queryMemory.sortExpression, skip: skip, take: take };
    const trips = await this.api.queryHolidayTrips(this.queryType, this.queryMemory.filter, params);
    return trips.map(t => this.extendTrip(t, highlight));
  }

  private extendTrip(trip: HolidayTripsQueryRecord, expression: string) {
    const converter = new TextHighlightValueConverter();
    const highlight = (line: string) => converter.toView(line, expression)?.map(s => s.highlight ? `<span class="--highlight">${s.substring}</span>` : s.substring).join('');
    trip['highlightedName'] = highlight(trip.name);
    trip['travelersNames'] = trip.travelers.map(t => highlight(t.name)).join(', ');
    trip['travelersInitials'] = trip.travelers.map(t => t.initials).join(', ');
    trip['travelersTitle'] = trip.travelers.map(t => t.name).join('\n');
    trip['countriesNames'] = trip.countries.map(c => highlight(c.name)).join(', ');
    trip['countriesFlags'] = trip.countries.filter(c => c.formFlag != null).map(c => `<i class="${c.formFlag}"></i>`).join(' ');
    trip['countriesTitle'] = trip.countries.map(c => c.name).join('\n');
    return trip;
  }

  get homeRoute() {
    return HOME_ROUTE;
  }

  get batchSize() {
    return BATCH_SIZE;
  }

  showTrip(trip: HolidayTripsQueryRecord) {
    this.router.navigate(`#/${HOME_ROUTE}/holidays/${trip.id}`);
  }
}
